// src/modules/app-views/crm/pages/Boards/tabs/AgendaTab/AgendaEventModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Badge, Col, DatePicker, Form, Input, Modal, Row, Select, TimePicker } from 'antd';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
export const EVENT_TYPES = [
  { key: 'reminder', color: 'gold' },
  { key: 'meeting', color: 'cyan' },
  { key: 'absence', color: 'red' },
  { key: 'deadline', color: 'volcano' },
  { key: 'task', color: 'green' },
  { key: 'other', color: 'purple' },
];

export const EVENT_COLOR_MAP = EVENT_TYPES.reduce((acc, t) => {
  acc[t.key] = t.color;
  return acc;
}, {});

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AgendaEventModal = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { show, setShow, event, selectedDate, onSave, isViewOnly } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!event;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;

    if (event) {
      form.setFieldsValue({
        title: event.title || '',
        date: event.date ? dayjs(event.date) : dayjs(),
        startTime: event.startTime ? dayjs(event.startTime, 'HH:mm') : null,
        endTime: event.endTime ? dayjs(event.endTime, 'HH:mm') : null,
        eventType: event.eventType || 'reminder',
        notes: event.notes || '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        date: selectedDate ? dayjs(selectedDate) : dayjs(),
        eventType: 'reminder',
      });
    }
  }, [show, event, selectedDate, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOk = async () => {
    if (isViewOnly) {
      setShow(false);
      return;
    }

    try {
      const values = await form.validateFields();
      onSave({
        _id: event?._id || event?.uid || null,
        title: values.title,
        date: values.date.format('YYYY-MM-DD'),
        startTime: values.startTime ? values.startTime.format('HH:mm') : null,
        endTime: values.endTime ? values.endTime.format('HH:mm') : null,
        eventType: values.eventType,
        notes: values.notes || '',
      });
      setShow(false);
    } catch {
      // validation error
    }
  };

  const handleCancel = () => setShow(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={show}
      title={translateX(isViewOnly ? 'view_event' : isEditing ? 'edit_event' : 'new_event')}
      okText={translateX(isViewOnly ? 'close' : 'save')}
      cancelText={isViewOnly ? undefined : translateX('cancel')}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnHidden
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="title"
          label={translateX('title')}
          rules={[{ required: true, message: () => translateX('required_field') }]}
        >
          <Input placeholder={translateX('title')} maxLength={100} disabled={isViewOnly} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="date"
              label={translateX('date')}
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <DatePicker className="w-100" format="DD/MM/YYYY" disabled={isViewOnly} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="startTime" label={translateX('start')}>
              <TimePicker className="w-100" format="HH:mm" minuteStep={5} disabled={isViewOnly} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="endTime" label={translateX('end')}>
              <TimePicker className="w-100" format="HH:mm" minuteStep={5} disabled={isViewOnly} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="eventType" label={translateX('type')}>
          <Select disabled={isViewOnly}>
            {EVENT_TYPES.map(t => (
              <Select.Option key={t.key} value={t.key}>
                <Badge color={t.color} />
                <span className="ml-2 text-capitalize font-weight-semibold">
                  {translateX(t.key)}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="notes" label={translateX('notes')}>
          <Input.TextArea rows={3} placeholder={translateX('notes')} maxLength={500} showCount disabled={isViewOnly} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AgendaEventModal;
